/**
 * Iter 58 — Mobile home with large tap-tiles.
 * Iter 118 — Tiles unified to the navy/slate brand theme (single accent).
 *           Added "Submit DPR" tile for field engineers.
 * Iter 175 — Home redesigned to a 2-cards-per-row grid (was a vertical list).
 *           Added "Create GRN" tile for stores/inventory users.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckSquare, Clock, Users, UserPlus, Smartphone, Calendar, Receipt, Fuel, PackageCheck, ScanFace, Check, ArrowRight, DoorOpen, CloudUpload, RefreshCw, UserCog, Plane, FileSignature, Handshake } from 'lucide-react';
import { Target } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { canRegisterLabour, canCreateGRN } from '../../components/MobileLayout';
import { loadFaceModels, getCurrentPosition } from '../../lib/faceRecognition';
import { apiGet } from '../../lib/api';
import { prefetchOfflineReferenceData, getLastSyncedAt } from '../../lib/offlineQueue';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';

const relativeTime = (iso) => {
  if (!iso) return null;
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} minute${m > 1 ? 's' : ''} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d > 1 ? 's' : ''} ago`;
};

const canVisitorTile = (user) => {
  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  return ['super_admin', 'admin', 'hr', 'md'].includes(role) || ['HR', 'ADMIN', 'MD', 'SECURITY'].includes(dept);
};

export default function MobileHome() {
  const { user } = useAuth() || {};
  const [faceStatus, setFaceStatus] = useState(null);
  const { pending, syncing, flush } = useNetworkStatus();
  const [lastSynced, setLastSynced] = useState(null);

  // Change 20 — fetch effective RBAC permissions once so we can gate the BD
  // tile based on the REAL permission resolution (role + per-user overrides),
  // not the lightweight role/department string-set pattern used elsewhere on
  // this page.
  const [perms, setPerms] = useState(null);
  useEffect(() => {
    if (user?.user_id) {
      apiGet(`/rbac/users/${user.user_id}/permissions`)
        .then(r => setPerms(r?.effective || {}))
        .catch(() => setPerms({}));
    }
  }, [user?.user_id]);
  const canBD = perms?.crm?.read === true;

  const refreshLastSynced = useCallback(() => {
    getLastSyncedAt().then(setLastSynced).catch(() => {});
  }, []);

  useEffect(() => {
    // Pre-warm face-api models in the background as soon as the user
    // lands on the mobile home — so by the time they tap Punch / Labour
    // the heavy weights are already cached and the camera starts instantly.
    loadFaceModels().catch(() => {});
    // Pre-warm GPS here too — the OS GPS chip is warming up while the user
    // reads the home screen. By the time they open Attendance (~3-5s), the
    // position is cached and the Capacitor/web layer returns it in <100ms.
    getCurrentPosition().catch(() => {});
    apiGet('/mobile/attendance/me/face-status').then(setFaceStatus).catch(() => {});
    // Invisible offline pre-caching of reference lists (4h TTL) so every
    // offline form opens with populated dropdowns. Silent on failure.
    prefetchOfflineReferenceData().catch(() => {});
    refreshLastSynced();
  }, [refreshLastSynced]);

  useEffect(() => { refreshLastSynced(); }, [pending, syncing, refreshLastSynced]);

  const onSyncNow = async () => {
    const res = await flush();
    refreshLastSynced();
    if (res?.synced > 0) toast.success(`Synced ${res.synced} entr${res.synced === 1 ? 'y' : 'ies'}`);
    else if (res?.failed > 0) toast.error(`${res.failed} entr${res.failed === 1 ? 'y' : 'ies'} failed — will retry`);
    else toast.info('Nothing to sync right now');
  };

  const tiles = [
    { to: '/m/my-tasks', icon: CheckSquare, title: 'My Tasks', desc: 'Today\'s assignments' },
    { to: '/m/attend', icon: Clock, title: 'Attendance', desc: 'Punch in & out · face' },
    { to: '/m/ess', icon: UserCog, title: 'My ESS', desc: 'Payslips · leave · records' },
    { to: '/m/approvals', icon: CheckSquare, title: 'Approvals', desc: 'Act on pending approvals' },
    { to: '/m/labour', icon: Users, title: 'Labour Kiosk', desc: 'Scan workers at gate' },
    ...(canCreateGRN(user) ? [{ to: '/m/grn', icon: PackageCheck, title: 'Create GRN', desc: 'Receive goods · evidence' }] : []),
    { to: '/m/leave', icon: Calendar, title: 'Apply Leave', desc: 'Request · balance' },
    { to: '/m/petty-cash', icon: Receipt, title: 'Petty Cash', desc: 'Expense · approvals' },
    { to: '/m/travel-request', icon: Plane, title: 'Travel Request', desc: 'Domestic · international' },
    ...((user?.employment_type === 'Consultant') ? [{ to: '/m/consultant-invoice', icon: FileSignature, title: 'Submit Invoice', desc: 'Service invoice + receipt' }] : []),
    { to: '/m/fuel', icon: Fuel, title: 'Fuel', desc: 'Refuelling log · fleet' },
    ...(canRegisterLabour(user) ? [{ to: '/m/labour-register', icon: UserPlus, title: 'Register Labour', desc: 'Enroll · face + KYC' }] : []),
    ...(canVisitorTile(user) ? [{ to: '/m/visitor', icon: DoorOpen, title: 'Visitor Check-in', desc: 'Register walk-ins' }] : []),
    // Change 20 — RBAC-gated tiles using REAL effective permissions
    ...(canBD ? [{ to: '/m/bd', icon: Handshake, title: 'Business Development', desc: 'Leads · pipeline · companies' }] : []),
  ];
  return (
    <div className="px-5 pt-5 pb-6">
      <header className="mb-5 bg-gradient-to-br from-[#1F7A6E] to-[#0F172A] text-white rounded-2xl p-5 shadow-lg" data-testid="m-home-welcome">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-display font-bold flex items-center gap-1.5">
          <Smartphone size={11} /> Field Companion
        </div>
        <h1 className="font-display font-bold text-2xl text-white mt-1 leading-tight">
          Welcome, <span className="text-[#9CA9D6]">{(user?.name || 'team').split(' ')[0]}</span>
        </h1>
        <p className="text-[11px] text-white/60 mt-1">Tap a card to get started · {user?.role?.replace(/_/g, ' ') || 'team'}</p>
      </header>

      {/* FY mission — shows the company goal (target only, no progress yet). */}
      <div data-testid="m-home-fy-target" className="mb-4 rounded-2xl border border-teal-200 bg-gradient-to-r from-teal-50 to-white px-4 py-3 flex items-center gap-3 shadow-sm">
        <span className="w-10 h-10 rounded-xl bg-[#1F7A6E] text-white hover:bg-[#14504E] flex items-center justify-center shrink-0">
          <Target size={20} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-teal-700 font-display font-bold">FY 2026-27 Mission</div>
          <div className="font-display font-extrabold text-slate-900 text-lg leading-tight">
            ₹1,000 Cr <span className="text-teal-700">Order Book</span>
          </div>
          <div className="text-[11px] text-slate-500 leading-snug">Every site, every entry — getting us there.</div>
        </div>
      </div>

      {/* Pending-sync nudge — visible even when online, in case auto-sync hasn't
          fired yet (e.g. app was minimised when connectivity returned). */}
      {pending > 0 && (
        <button
          type="button"
          onClick={onSyncNow}
          disabled={syncing}
          data-testid="m-home-sync-nudge"
          className="w-full mb-4 flex items-center gap-3 rounded-xl bg-teal-50 border border-teal-300 px-3.5 py-3 text-left active:scale-[0.98] transition-transform disabled:opacity-70"
        >
          <span className="w-9 h-9 rounded-lg bg-teal-600 text-white flex items-center justify-center shrink-0">
            {syncing ? <RefreshCw size={17} className="animate-spin" /> : <CloudUpload size={17} />}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-display font-bold text-[13px] text-teal-900 leading-tight">
              {syncing ? 'Syncing…' : `${pending} entr${pending === 1 ? 'y' : 'ies'} pending sync`}
            </span>
            <span className="block text-[11px] text-teal-700/80 leading-snug">
              {syncing ? 'Uploading your offline entries' : 'Tap to sync now'}
            </span>
          </span>
          {!syncing && <ArrowRight size={16} className="text-teal-600 shrink-0" />}
        </button>
      )}

      {lastSynced && (
        <p className="-mt-1 mb-4 text-[11px] text-slate-400 font-display flex items-center gap-1.5" data-testid="m-home-last-synced">
          <RefreshCw size={11} /> Last synced {relativeTime(lastSynced)}
        </p>
      )}

      {faceStatus?.linked && (
        faceStatus.enrolled ? (
          <div
            className="mb-4 flex items-center gap-2.5 rounded-xl bg-emerald-50 border border-emerald-200 px-3.5 py-2.5"
            data-testid="m-face-status-enrolled"
          >
            <span className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center shrink-0">
              <Check size={16} strokeWidth={3} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-[13px] text-emerald-800 leading-tight flex items-center gap-1.5">
                <ScanFace size={13} /> Face enrolled
              </div>
              <div className="text-[11px] text-emerald-700/80 leading-snug">
                {faceStatus.descriptor_count} pose{faceStatus.descriptor_count === 1 ? '' : 's'} saved · attendance ready
              </div>
            </div>
          </div>
        ) : (
          <Link
            to="/m/enroll"
            className="mb-4 flex items-center gap-2.5 rounded-xl bg-amber-50 border border-amber-300 px-3.5 py-2.5 active:scale-[0.98] transition-transform"
            data-testid="m-face-status-pending"
          >
            <span className="w-8 h-8 rounded-lg bg-[#1F7A6E] text-white flex items-center justify-center shrink-0">
              <ScanFace size={16} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-[13px] text-amber-900 leading-tight">Face not enrolled</div>
              <div className="text-[11px] text-amber-800/80 leading-snug">Capture your 3 poses so attendance can be logged.</div>
            </div>
            <span className="text-amber-700 shrink-0"><ArrowRight size={16} /></span>
          </Link>
        )
      )}

      <div className="grid grid-cols-3 gap-2.5" data-testid="mobile-home-tiles">
        {tiles.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            data-testid={`m-tile-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className="group bg-white border border-slate-200 hover:border-[#1F7A6E]/60 rounded-2xl p-3 flex flex-col gap-2 active:scale-[0.97] transition-all shadow-sm hover:shadow-md min-h-[108px]"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1F7A6E] to-[#0F172A] text-white flex items-center justify-center shrink-0">
              <t.icon size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-[13px] text-slate-900 leading-tight line-clamp-2">{t.title}</div>
              <div className="text-[10px] text-slate-500 mt-1 leading-snug line-clamp-2">{t.desc}</div>
            </div>
          </Link>
        ))}
      </div>
      <p className="mt-6 text-[10px] text-center text-slate-400 font-display">
        ELR Industries · v2026.5.2
      </p>
    </div>
  );
}
